import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './scss/BoardVisibility.scss';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { config } from '../../config';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { api } from '../../utils/apiUtils';

const BoardVisibility = ({ boardId, visibility, setBoard, board, workspaceName, currentUserRole }) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);

    // Determine if current user is admin (board admin or workspace admin)
    const isAdmin = useMemo(() => {
        if (!board || !user) return false;

        const userId = user?.user?._id;

        // Check if user is board admin
        const boardMember = board.members?.find(m => m.user?._id === userId || m.user === userId);
        const isBoardAdmin = boardMember?.role === 'admin';

        // Check if user is workspace admin (via workspaceRole)
        const isWorkspaceAdmin = boardMember?.workspaceRole === 'admin';

        return isBoardAdmin || isWorkspaceAdmin || currentUserRole === 'admin';
    }, [board, user, currentUserRole]);

    // Get workspace name for description
    const workspaceDisplayName = useMemo(() => {
        return workspaceName || board?.workspace?.name || 'the Workspace';
    }, [workspaceName, board]);

    const visibilityOptions = useMemo(() => [
        {
            name: 'Private',
            description:
                'Only board members can see this board. Workspace admins can close the board or remove members.',
            icon: 'lock',
        },
        {
            name: 'Workspace',
            description:
                `All members of ${workspaceDisplayName} can see and edit this board.`,
            icon: 'group', 
        },
        {
            name: 'Public',
            description:
                'Anyone on the internet can see this board. Only board members can edit.',
            icon: 'globe',
        },
    ], [workspaceDisplayName]);

    const handleBoardVisibility = async (option) => {
        // Prevent action if disabled or already selected
        if (option.disabled || option.name === visibility || isUpdating) return;

        // Check if user has permission to change visibility
        if (!isAdmin) {
            showError(dispatch, 'Only board admins can change board visibility');
            return;
        }

        const oldVisibility = visibility;
        setIsUpdating(true);

        try {
            // Optimistic update - immediately update UI
            setBoard((prevBoard) => ({
                ...prevBoard,
                visibility: option.name,
            }));

            // Make API call
            await api.put(
                `${config.API_URI}/v1/boards/${boardId}/visibility`,
                { visibility: option.name }
            );

            // Success feedback
            showSuccess(dispatch, `Board visibility changed to ${option.name}`);
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));

        } catch (error) {
            // Rollback on error
            setBoard((prevBoard) => ({
                ...prevBoard,
                visibility: oldVisibility,
            }));

            const errorMessage = error.message || 'Failed to update board visibility';
            showError(dispatch, errorMessage);
            console.error('Error updating board visibility:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="change-visibility">
            <header className="change-visibility__header">
                <h3>Change visibility</h3>
                {!isAdmin && (
                    <p className="change-visibility__permission-notice">
                        Only board admins can change visibility
                    </p>
                )}
            </header>
            <ul className="change-visibility__options">
                {visibilityOptions.map((option) => {
                    const isSelected = visibility === option.name;
                    const isDisabled = !isAdmin || isUpdating;

                    return (
                        <li
                            key={option.name}
                            className={`change-visibility__option ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleBoardVisibility(option)}
                            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                            <span className="material-symbols-outlined">{option.icon}</span>
                            <div className="details">
                                <h3>{option.name}</h3>
                                <p>{option.description}</p>
                            </div>
                            
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default BoardVisibility;
